import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ScoreCard } from '@/components/ScoreCard';
import { RecommendationsList } from '@/components/RecommendationsList';
import { AuditFormData, AuditResult } from '@/lib/schemas';
import { getAuditApiUrl } from '@/lib/config';
import { trackAuditResultsView, trackBackToHome, trackGenerateReport, trackDownloadReport, trackEmailReport } from '@/lib/analytics';
import { ArrowLeft, FileText, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

export default function ResultPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<AuditFormData | null>(null);
  const [result, setResult] = useState<AuditResult | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isEmailing, setIsEmailing] = useState(false);

  useEffect(() => {
    // Get data from sessionStorage
    const storedFormData = sessionStorage.getItem('auditData');
    const storedResult = sessionStorage.getItem('auditResult');

    if (!storedFormData || !storedResult) {
      // Redirect to audit page if no data found
      navigate('/audit');
      return;
    }

    try {
      const parsedFormData = JSON.parse(storedFormData);
      const parsedResult = JSON.parse(storedResult);
      
      setFormData(parsedFormData);
      setResult(parsedResult);
      
      // Track audit results page view
      trackAuditResultsView(parsedFormData.company_name, parsedResult.score);
    } catch (error) {
      console.error('Error parsing stored data:', error);
      navigate('/audit');
    }
  }, [navigate]);

  const handleDownloadPDF = async () => {
    if (!formData || !result) return;

    // Track the download request
    trackGenerateReport(formData.company_name, result.score);
    setIsDownloading(true);
    
    try {
      const response = await fetch(getAuditApiUrl.pdf(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          form_data: formData,
          result,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }

      // Create download link
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-report-${formData.company_name.replace(/[^a-z0-9]/gi, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      // Track successful download
      trackDownloadReport(formData.company_name, result.score);
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download PDF. Please try again.');
      console.error('PDF download error:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!formData || !result) return;

    // Track the email request
    trackEmailReport(formData.company_name, result.score);
    setIsEmailing(true);
    
    try {
      const response = await fetch(getAuditApiUrl.email(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          form_data: formData,
          result,
        }),
      });

      if (response.ok) {
        toast.success('Report sent successfully! Check your email.');
      } else {
        throw new Error('Failed to send email');
      }
    } catch (error) {
      toast.error('Failed to send email. Please try again.');
      console.error('Email sending error:', error);
    } finally {
      setIsEmailing(false);
    }
  };

  // Show loading state while data is being loaded
  if (!formData || !result) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading your results...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-gray-50">
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1">
              Your Audit Results
            </h1>
            <p className="text-base text-brand-gray">
              Digital Efficiency Assessment for {formData.company_name}
            </p>
          </div>
          <Link to="/">
            <Button 
              size="sm" 
              className="text-white bg-brand-orange hover:bg-brand-orange/90 transition-colors duration-200"
              onClick={() => trackBackToHome(formData?.company_name, result?.score)}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              <span className="font-medium">Back to Home</span>
            </Button>
          </Link>
        </div>

        <ScoreCard result={result} companyName={formData.company_name} />
        
        <RecommendationsList recommendations={result.recommendations} />

        <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
          <Button 
            onClick={handleDownloadPDF} 
            disabled={isDownloading}
            size="lg"
            className="bg-brand-orange hover:bg-brand-orange/90 text-white px-6 py-3 rounded-lg transition-colors duration-200"
          >
            <FileText className="mr-2 h-5 w-5" />
            {isDownloading ? 'Generating PDF...' : 'Download PDF Report'}
          </Button>
          
          <Button 
            onClick={handleSendEmail} 
            disabled={isEmailing}
            variant="outline"
            size="lg"
            className="border-brand-orange/30 text-brand-orange bg-gradient-to-br from-orange-50 to-orange-100 hover:bg-gradient-to-br hover:from-brand-orange hover:to-brand-orange/90 hover:text-white hover:border-brand-orange px-6 py-3 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <Send className="mr-2 h-5 w-5" />
            {isEmailing ? 'Sending...' : 'Email My Report'}
          </Button>
        </div>

        {/* Company recap */}
        <div className="mt-12 p-6 bg-white/80 backdrop-blur-sm rounded-2xl border-0 shadow-lg">
          <h3 className="text-lg font-semibold mb-4 text-black">
            Assessment Summary
          </h3>
          <div className="grid md:grid-cols-2 gap-4 text-gray-600">
            <div>
              <strong className="text-brand-dark">Contact:</strong> {formData.contact_name}
            </div>
            <div>
              <strong className="text-brand-dark">Industry:</strong> {formData.industry}
            </div>
            <div>
              <strong className="text-brand-dark">Current Systems:</strong> {formData.current_systems}
            </div>
            <div>
              <strong className="text-brand-dark">Assessment Date:</strong> {new Date().toLocaleDateString()}
            </div>
          </div>
          {formData.top_pain_points && (
            <div className="mt-4">
              <strong className="text-brand-dark">Top Pain Points:</strong>
              <p className="text-brand-gray mt-2 leading-relaxed">{formData.top_pain_points}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
